import { computed, ref, isRef, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import axios from "axios";

import {
  setUserSession,
  clearUserSession,
  getUserData,
} from "@/stores/localStorage.js";

import usePermissionStore from "@/stores/permission.store";

import { useAuthStore } from "../../stores/auth.store";
import { storeToRefs } from "pinia";

const hasRoles = (roles) =>
  roles.some((role) => {
    if (getUserData().roles.some((userRole) => userRole.name === role)) {
      return true;
    }
  });

const userHasRoles = (user, roles) => {
  if (
    typeof user === "object" &&
    Array.isArray(user.roles) &&
    user.roles.length > 0
  ) {
    return roles.some((role) => {
      if (user.roles.some((userRole) => userRole.name === role)) {
        return true;
      }
    });
  }
};

const hasPermission = (permission) => {
  const userPermissions = JSON.parse(localStorage.getItem("permissions"));

  if (hasRoles(["superadmin"])) {
    return true;
  }

  if (typeof permission === "string") {
    return userPermissions.some(
      (userPermission) => userPermission.name == permission
    );
  }

  if (Array.isArray(permission)) {
    return permission.reduce((acc, currentVal, index) => {
      return acc && hasPermission(permission[index]);
    }, true);
  }
};

const hasAnyPermission = (permission) => {
  const userPermissions = JSON.parse(localStorage.getItem("permissions"));

  if (hasRoles(["superadmin"])) {
    return true;
  }

  if (typeof permission === "string") {
    return userPermissions.some(
      (userPermission) => userPermission.name == permission
    );
  }

  if (Array.isArray(permission)) {
    return permission.reduce((acc, currentVal, index) => {
      return acc || hasPermission(permission[index]);
    }, false);
  }
};

const userHasAnyPermission = (user, permission) => {
  if (userHasRoles(user, ["superadmin"])) {
    return true;
  }

  if (
    typeof user === "object" &&
    isArray(user.permisos) &&
    user.permisos.length > 0
  ) {
    if (typeof permission === "string") {
      return user.permisos.some(
        (userPermission) => userPermission.name == permission
      );
    }

    if (Array.isArray(permission)) {
      return user.permisos.reduce((acc, currentVal, index) => {
        return acc || hasPermission(permission[index]);
      }, false);
    }
  }

  return false;
};

const getPermissions = () => {
  return getUserData().permissions;
};

const current_user = () => {
  return getUserData();
};


// ** Requsts ====================================================================

const fetchAllPermissions = async () => {
  const { data } = await axios.get("/api/v1/permissions");
  return data;
};

// ** Composable =================================================================

const useAvatar = () => {
  const store = useAuthStore();
  const { avatar } = storeToRefs(store);

  return {
    getAvatar: () => avatar?.value?.original,
    getAvatarThumbnail: () => avatar?.value?.thumbnail,
  }
}

const useRolePermissions = (_enabled = false) => {
  const enabled = computed(() => (isRef(_enabled) ? _enabled.value : _enabled));
  const allPermissionsList = ref([]);

  const store = usePermissionStore();

  // ** Query ====================================================================
  const {
    data: fetchPermissionsData,
    isSuccess: fetchPermissionsIsSuccess,
    isError: fetchPermissionsIsError,
    isLoading: fetchPermissionsIsLoading,
    error: fetchPermissionError,
  } = useQuery({
    queryKey: ["allPermissionList"],
    queryFn: () => fetchAllPermissions(),
    enabled: enabled.value,
  });

  // ** Watch ====================================================================

  watch(
    fetchPermissionsData,
    (newData) => {
      if (newData?.data) store.setPermissionList(newData?.data);
    },
    { immediate: true }
  );

  return {
    allPermissionsList,
    fetchPermissionsIsSuccess,
    fetchPermissionsIsError,
    fetchPermissionsIsLoading,
    fetchPermissionError,
  };
};

export {
  useRolePermissions,
  hasAnyPermission,
  hasPermission,
  hasRoles,
  current_user,
  userHasAnyPermission,
  getPermissions,
  userHasRoles,

  useAvatar,
};
